/* Messages — conversation list page (iOS-style, gold + navy theme).
   Slim orchestrator; header/list/dialog live under ./messagesList/*. */
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "@phosphor-icons/react";
import { toast } from "sonner";

import { deleteConversation } from "../lib/messagingApi";
import { useConversations, invalidateConversations } from "../lib/conversationsStore";
import { useUser } from "../context/UserAuthContext";
import { VOICE_CALLS_ENABLED } from "../lib/featureFlags";

import ConversationList from "./messagesList/ConversationList";
import DeleteConfirmDialog from "./messagesList/DeleteConfirmDialog";
import TabsBar from "./messagesList/TabsBar";
import TopHeader from "./messagesList/TopHeader";
import { useLiveConversationUpdates } from "./messagesList/useLiveConversationUpdates";

// P2 — CallsTab is only rendered when voice-calls are enabled AND
// tab==='calls'. Both are false by default (Feb 2026) so pulling
// ~270 lines of JSX into the initial bundle wastes parse time.
// Lazy import defers that cost to actual click.
const CallsTab = lazy(() => import("../components/calls/CallsTab"));

export default function MessagesList() {
  const { user } = useUser();
  const navigate = useNavigate();
  // P0 — shared store: single fetch across mount + re-visits within
  // 30 s. Delete + SSE events invalidate the cache explicitly below.
  const { items, loading } = useConversations();
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("chats");
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(72);

  // Real-time: WhatsApp-style instant patch + typing indicator per row.
  const { typingByConv } = useLiveConversationUpdates(user?.id);

  const handleRowSelect = (c) => {
    // Optimistic unread reset happens in ConversationList's own onSelect;
    // we no longer need to mutate the shared store here — the next
    // SSE conversation:read event (or a manual refresh) reconciles.
    void c;
  };

  // PERMANENT delete — optimistically yanks the row so the UI feels
  // instant; rolls back on error.
  const performDelete = async (c) => {
    if (!c) return;
    setDeleting(true);
    setConfirmDel(null);
    try {
      await deleteConversation(c.id);
      await invalidateConversations();
      toast.success("تم حَذف المُحادثة نهائياً");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر الحَذف");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = items.filter((c) => {
    if (!q.trim()) return true;
    const text = q.trim().toLowerCase();
    const title = c.kind === "group" ? (c.title || "") : (c.counterpart?.name || "");
    return title.toLowerCase().includes(text)
      || (c.last_message_preview || "").toLowerCase().includes(text);
  });

  const totalUnread = items.reduce((s, c) => s + (c.unread || 0), 0);
  const showSearch = items.length >= 2;

  // Live header height — grows when search bar renders / wraps.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () => setHeaderH(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showSearch]);

  // Match body + status-bar to the home-page white so the iOS dead-zone
  // reads as continuous with the header.
  useEffect(() => {
    const prevBodyBg = document.body.style.backgroundColor;
    const prevHtmlBg = document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = "#FFFFFF";
    document.documentElement.style.backgroundColor = "#FFFFFF";
    const meta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "#FFFFFF");
    return () => {
      document.body.style.backgroundColor = prevBodyBg;
      document.documentElement.style.backgroundColor = prevHtmlBg;
      if (meta && prevTheme !== null) meta.setAttribute("content", prevTheme);
    };
  }, []);

  return (
    <>
      <div
        className="lg:hidden fixed pointer-events-none -z-10"
        style={{
          top: "-40vh", bottom: "-20vh", left: 0, right: 0,
          background:
            "linear-gradient(180deg,#FFFFFF 0%,#FFFFFF 70%,#F4F4F2 90%,#EDEDED 100%)",
        }}
        aria-hidden="true"
      />
      <div
        className="min-h-[100dvh] px-4 pb-32 font-tajawal"
        dir="rtl"
        data-testid="messages-list-page"
        style={{ paddingTop: `${headerH + 8}px` }}
      >
        <TopHeader
          ref={headerRef}
          tab={tab}
          totalUnread={totalUnread}
          showSearch={showSearch}
          q={q}
          setQ={setQ}
          onBack={() => navigate("/")}
        />

        <TabsBar tab={tab} setTab={setTab} />

        <main>
          {VOICE_CALLS_ENABLED && tab === "calls" ? (
            <Suspense fallback={null}>
              <CallsTab />
            </Suspense>
          ) : (
            <ConversationList
              loading={loading}
              items={items}
              filtered={filtered}
              myId={user?.id}
              typingByConv={typingByConv}
              onSelect={handleRowSelect}
              onRequestDelete={setConfirmDel}
            />
          )}
        </main>

        <DeleteConfirmDialog
          conv={confirmDel}
          deleting={deleting}
          onCancel={() => setConfirmDel(null)}
          onConfirm={performDelete}
        />

        <div
          className="fixed left-0 right-0 text-center text-[10.5px] text-[#5A6470] flex items-center justify-center gap-1"
          style={{ bottom: "calc(env(safe-area-inset-bottom) + 110px)" }}
        >
          <Lock size={11} weight="bold" /> رسائل مَحفوظة بِسرّيّة على خوادم LexIQ
        </div>
      </div>
    </>
  );
}
